import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { trackAddToCart } from '../../utils/metaPixel';
interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  withArrow?: boolean;
  bounce?: boolean;
  href?: string;
  trackCart?: boolean; // Explicitly control AddToCart tracking
  children: React.ReactNode;
}
export function Button({
  variant = 'primary',
  size = 'md',
  withArrow = false,
  bounce = false,
  href,
  trackCart = false,
  children,
  className = '',
  onClick,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center rounded-full font-bold transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer uppercase tracking-wide';
  const variants = {
    primary: 'bg-[#F97316] text-white hover:bg-[#EA580C] shadow-lg hover:shadow-xl focus:ring-[#F97316] border border-transparent',
    secondary: 'bg-[#064E3B] text-white hover:bg-[#0D6B4F] shadow-md hover:shadow-lg focus:ring-[#064E3B] border border-transparent',
    outline: 'bg-transparent border-2 border-[#0D6B4F] text-[#0D6B4F] hover:bg-[#0D6B4F] hover:text-white focus:ring-[#0D6B4F]'
  };
  const sizes = {
    sm: 'px-5 py-2.5 text-sm',
    md: 'px-7 py-3.5 text-base',
    lg: 'px-8 py-4 text-lg md:text-xl w-full md:w-auto'
  };
  const bounceAnimation = bounce ? {
    y: [0, -4, 0],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatDelay: 3,
      ease: 'easeInOut'
    }
  } : {};
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (href) {
      e.preventDefault();
      // Check if it's an external URL
      if (href.startsWith('http://') || href.startsWith('https://')) {
        if (trackCart) {
          // Wait for tracking to complete before redirecting
          await trackAddToCart({
            content_name: 'Protocolo Metabo-Reset 40+',
            content_ids: ['44441705480271'],
            content_type: 'product',
            value: 10.0,
            currency: 'USD'
          });
        }
        // Redirect after tracking is confirmed sent
        window.location.href = href;
      } else {
        // Internal anchor link
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }
    }
    onClick?.(e as any);
  };
  return <motion.button animate={bounceAnimation} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`} onClick={handleClick} {...props} data-id="element-204">
      {children}
      {withArrow && <ArrowRight className="ml-2 h-5 w-5" data-id="element-205" />}
    </motion.button>;
}